import { Command } from 'commander';
import { FileSystemAdapter } from '../../src/utils/file-system-adapter.js';
import { PathnameService } from '../../src/services/pathname-service.js';
import { IntentService } from '../../src/services/intent-service.js';
import { FileService } from '../../src/services/file-service.js';
import { loadLlmailConfig } from '../../src/utils/config.js';
import { logger } from '../../src/utils/logger.js';
import { generateId } from '../../src/utils/id-generator.js';
import { FileFormatValidator } from '../../src/validation/file-format-validator.js';
import { MetadataFormatValidator } from '../../src/validation/metadata-format-validator.js';
import { FileSystemValidator } from '../../src/validation/file-system-validator.js';
import { IssueService } from '../../src/services/issue-service.js';
import { LLMail } from '../../src/LLMail.js';

export function createNewCommand(fsAdapter: FileSystemAdapter): Command {
  const command = new Command('new')
    .description('Create a new issue')
    .argument('<type>', 'Type of issue to create')
    .option('-s, --state <state>', 'Initial state')
    .option('-t, --title <title>', 'Title for the issue')
    .option('-c, --content <content>', 'Initial content')
    .option('-f, --frontmatter <frontmatter>', 'JSON string for frontmatter fields')
    .action(async (type, options) => {
      try {
        // Initialize LLMail with config and plugins
        const config = await loadLlmailConfig(fsAdapter);
        const llmail = new LLMail({ fs: fsAdapter, config });
        await llmail.init();

        // Get the plugin service from LLMail
        const pluginService = llmail.getPluginService();

        // Initialize services with plugin service
        const intentService = new IntentService(config, pluginService);
        const pathnameService = new PathnameService(config, intentService);
        const fileService = new FileService(fsAdapter, pathnameService);
        const fileFormatValidator = new FileFormatValidator();
        const metadataValidator = new MetadataFormatValidator();
        const fileSystemValidator = new FileSystemValidator(fsAdapter);
        const issueService = new IssueService(
          pathnameService,
          fsAdapter,
          intentService,
          fileService,
          fileFormatValidator,
          metadataValidator,
          fileSystemValidator,
          pluginService
        );

        // Parse user frontmatter (if any) from JSON:
        let userFrontmatter: Record<string, any> = {};
        if (options.frontmatter) {
          try {
            userFrontmatter = JSON.parse(options.frontmatter);
          } catch (parseErr) {
            logger.error('Invalid JSON for --frontmatter');
            throw parseErr;
          }
        }

        // Apply the `--title` as a frontmatter field if present:
        if (options.title) {
          userFrontmatter.title = options.title;
        }

        const result = await issueService.fileMethods.writeNewFile({
          id: generateId(),
          type,
          active: true,
          state: options.state,
          ...userFrontmatter
        }, options.content || '');

        logger.success(`Created new issue at ${result}`);
      } catch (err) {
        logger.error(err instanceof Error ? err.message : String(err));
        throw err;
      }
    });

  return command;
}

/**
 * Create a new issue
 */
export function newCommand(fsAdapter: FileSystemAdapter): Command {
  return createNewCommand(fsAdapter);
} 